import { keepPreviousData, useQuery } from "@tanstack/react-query";
import URI from "urijs";
import cloneDeep from "lodash/cloneDeep";
import concat from "lodash/concat";
import pick from "lodash/pick";
import omit from "lodash/omit";
import { EventsDefinitions, EventsEntityTypes } from "@graylog/server-api";
import i18n from "i18n";
import { qualifyUrl } from "util/URLUtils";
import PaginationURL from "util/PaginationURL";
import UserNotification from "util/UserNotification";
import fetch from "logic/rest/FetchProvider";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { defaultOnError } from "util/conditional/onError";
import FiltersForQueryParams from "components/common/EntityFilters/FiltersForQueryParams";
import CurrentUser from "logic/users/CurrentUser";
import EventDefinitionTagsFilter from "components/event-definitions/EventDefinitionTagsFilter";
import EventDefinitionTypeFilter from "components/event-definitions/EventDefinitionTypeFilter";
export const EVENT_DEFINITIONS_QUERY_KEY = ["event-definitions"];
const sourceUrl = "/events/definitions";
const eventDefinitionsUrl = ({
  segments = [],
  query = {}
}) => {
  const uri = new URI(sourceUrl);
  const nextSegments = concat(uri.segment(), segments);
  uri.segmentCoded(nextSegments);
  uri.query(query);
  return qualifyUrl(uri.resource());
};
const setAlertFlag = (eventDefinition) => {
  var _a;
  const isAlert = ((_a = eventDefinition.notifications) != null ? _a : []).length > 0;
  return { ...eventDefinition, alert: isAlert };
};
const extractSchedulerInfo = (eventDefinition) => {
  const clonedEventDefinition = cloneDeep(eventDefinition);
  const { _is_scheduled } = pick(clonedEventDefinition.config, ["_is_scheduled"]);
  clonedEventDefinition.config = omit(clonedEventDefinition.config, ["_is_scheduled"]);
  return { eventDefinition: clonedEventDefinition, isScheduled: _is_scheduled != null ? _is_scheduled : true };
};
const TAGS_ATTRIBUTE = {
  id: "tags",
  title: "Tags",
  type: "STRING",
  sortable: false,
  searchable: true,
  filterable: true,
  filter_component: EventDefinitionTagsFilter
};
export const fetchEventDefinitions = (searchParams) => {
  CurrentUser.reload();
  const url = PaginationURL(`${sourceUrl}/paginated`, searchParams.page, searchParams.pageSize, searchParams.query, {
    sort: searchParams == null ? void 0 : searchParams.sort.attributeId,
    order: searchParams == null ? void 0 : searchParams.sort.direction,
    filters: FiltersForQueryParams(searchParams.filters)
  });
  return fetch("GET", qualifyUrl(url)).then((response) => {
    const { elements, query, attributes, pagination } = response;
    const { count, total, page, per_page: perPage } = pagination;
    return {
      list: elements,
      pagination: {
        count,
        total,
        page,
        perPage,
        query
      },
      // The backend marks `type` (config.type) filterable; attach the custom filter_component
      // here so the dropdown lists plugin-provided type display names instead of a text input.
      attributes: [
        ...(attributes != null ? attributes : []).map(
          (attribute) => attribute.id === "type" ? { ...attribute, filter_component: EventDefinitionTypeFilter } : attribute
        ),
        TAGS_ATTRIBUTE
      ]
    };
  });
};
export const fetchAllEventDefinitions = () => fetch("GET", eventDefinitionsUrl({ query: { per_page: 0 } }));
export const getEventDefinition = (eventDefinitionId) => fetch("GET", eventDefinitionsUrl({ segments: [eventDefinitionId, "with-context"] })).then(
  (response) => ({
    eventDefinition: response.event_definition,
    context: response.context,
    is_mutable: response.is_mutable
  }),
  (error) => {
    if (error.status === 404) {
      UserNotification.error(
        i18n.t("hook_notifications.not_found", { ns: "event_definitions", id: eventDefinitionId }),
        i18n.t("hook_notifications.not_found_title", { ns: "event_definitions" })
      );
    }
    throw error;
  }
);
export const createEventDefinition = (newEventDefinition) => {
  const { eventDefinition, isScheduled } = extractSchedulerInfo(newEventDefinition);
  return fetch("POST", eventDefinitionsUrl({ query: { schedule: isScheduled } }), setAlertFlag(eventDefinition)).then(
    (response) => {
      UserNotification.success(
        i18n.t("hook_notifications.create_success_title", { ns: "event_definitions" }),
        i18n.t("hook_notifications.create_success", { ns: "event_definitions", title: eventDefinition.title })
      );
      return response;
    },
    (error) => {
      if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
        UserNotification.error(
          i18n.t("hook_notifications.create_failed", { ns: "event_definitions", title: eventDefinition.title, error }),
          i18n.t("hook_notifications.create_failed_title", { ns: "event_definitions" })
        );
      }
      throw error;
    }
  );
};
export const copyEventDefinition = (eventDefinition) => EventsDefinitions.duplicate(encodeURIComponent(eventDefinition.id)).then(
  (response) => {
    UserNotification.success(
      i18n.t("hook_notifications.duplicate_success_title", { ns: "event_definitions" }),
      i18n.t("hook_notifications.duplicate_success", { ns: "event_definitions", title: response.title })
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        i18n.t("hook_notifications.duplicate_failed", {
          ns: "event_definitions",
          title: eventDefinition.title,
          error
        }),
        i18n.t("hook_notifications.duplicate_failed_title", { ns: "event_definitions" })
      );
    }
    throw error;
  }
);
export const updateEventDefinition = (eventDefinitionId, updatedEventDefinition) => {
  const { eventDefinition, isScheduled } = extractSchedulerInfo(updatedEventDefinition);
  return fetch(
    "PUT",
    eventDefinitionsUrl({ segments: [eventDefinitionId], query: { schedule: isScheduled } }),
    setAlertFlag(eventDefinition)
  ).then(
    (response) => {
      UserNotification.success(
        i18n.t("hook_notifications.update_success_title", { ns: "event_definitions" }),
        i18n.t("hook_notifications.update_success", { ns: "event_definitions", title: eventDefinition.title })
      );
      return response;
    },
    (error) => {
      if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
        UserNotification.error(
          i18n.t("hook_notifications.update_failed", { ns: "event_definitions", title: eventDefinition.title, error }),
          i18n.t("hook_notifications.update_failed_title", { ns: "event_definitions" })
        );
      }
      throw error;
    }
  );
};
export const deleteEventDefinition = (eventDefinition) => EventsDefinitions.remove(encodeURIComponent(eventDefinition.id));
export const enableEventDefinition = (eventDefinition) => EventsDefinitions.schedule(encodeURIComponent(eventDefinition.id)).then(
  (response) => {
    UserNotification.success(
      i18n.t("hook_notifications.enable_success_title", { ns: "event_definitions" }),
      i18n.t("hook_notifications.enable_success", { ns: "event_definitions", title: eventDefinition.title })
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        i18n.t("hook_notifications.enable_failed", { ns: "event_definitions", title: eventDefinition.title, error }),
        i18n.t("hook_notifications.enable_failed_title", { ns: "event_definitions" })
      );
    }
    throw error;
  }
);
export const disableEventDefinition = (eventDefinition) => EventsDefinitions.unschedule(encodeURIComponent(eventDefinition.id)).then(
  (response) => {
    UserNotification.success(
      i18n.t("hook_notifications.disable_success_title", { ns: "event_definitions" }),
      i18n.t("hook_notifications.disable_success", { ns: "event_definitions", title: eventDefinition.title })
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        i18n.t("hook_notifications.disable_failed", { ns: "event_definitions", title: eventDefinition.title, error }),
        i18n.t("hook_notifications.disable_failed_title", { ns: "event_definitions" })
      );
    }
    throw error;
  }
);
export const clearNotificationQueue = (eventDefinition) => EventsDefinitions.clearNotificationQueue(encodeURIComponent(eventDefinition.id)).then(
  (response) => {
    UserNotification.success(
      i18n.t("hook_notifications.clear_queue_success_title", { ns: "event_definitions" }),
      i18n.t("hook_notifications.clear_queue_success", { ns: "event_definitions" })
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        i18n.t("hook_notifications.clear_queue_failed", { ns: "event_definitions", error }),
        i18n.t("hook_notifications.clear_queue_failed_title", { ns: "event_definitions" })
      );
    }
    throw error;
  }
);
export const keyFn = (searchParams) => [
  ...EVENT_DEFINITIONS_QUERY_KEY,
  "overview-paginated",
  searchParams
];
export const useEventDefinitionWithContext = (eventDefinitionId) => {
  const { data, isFetching } = useQuery({
    queryKey: [...EVENT_DEFINITIONS_QUERY_KEY, eventDefinitionId],
    queryFn: () => defaultOnError(
      getEventDefinition(eventDefinitionId),
      i18n.t("hook_notifications.load_one_failed", { ns: "event_definitions" }),
      i18n.t("hook_notifications.load_failed_title", { ns: "event_definitions" })
    ),
    retry: false
  });
  return {
    data: isFetching ? null : data,
    isFetching
  };
};
export const useAllEventDefinitions = () => useQuery({
  queryKey: [...EVENT_DEFINITIONS_QUERY_KEY, "all"],
  queryFn: fetchAllEventDefinitions
});
const useEventDefinitions = (searchParams, { enabled } = { enabled: true }) => {
  const { data, refetch, isInitialLoading } = useQuery({
    queryKey: [...EVENT_DEFINITIONS_QUERY_KEY, "paginated", searchParams],
    queryFn: () => defaultOnError(
      fetchEventDefinitions(searchParams),
      i18n.t("hook_notifications.load_list_failed", { ns: "event_definitions" }),
      i18n.t("hook_notifications.load_failed_title", { ns: "event_definitions" })
    ),
    placeholderData: keepPreviousData,
    retry: false,
    enabled
  });
  return {
    data,
    refetch,
    isInitialLoading
  };
};
export default useEventDefinitions;
export function useGetEntityTypes() {
  const { data, isLoading } = useQuery({
    // Deliberately not under EVENT_DEFINITIONS_QUERY_KEY: this is not event definition
    // entity data, so it should not be refetched on event definition mutations.
    queryKey: ["event-definition-entity-types"],
    queryFn: () => defaultOnError(
      EventsEntityTypes.all(),
      i18n.t("hook_notifications.entity_types_failed", { ns: "event_definitions" }),
      i18n.t("hook_notifications.entity_types_failed_title", { ns: "event_definitions" })
    )
  });
  return {
    entityTypes: isLoading ? [] : data || [],
    loadingEntityTypes: isLoading
  };
}
export function useGetListEventsClusterConfig() {
  const { data, isLoading } = useQuery({
    // Deliberately not under EVENT_DEFINITIONS_QUERY_KEY: this is cluster configuration,
    // not event definition entity data, so it should not be refetched on event definition mutations.
    queryKey: ["events-cluster-config"],
    queryFn: () => defaultOnError(
      ConfigurationsActions.listEventsClusterConfig(),
      i18n.t("hook_notifications.cluster_config_failed", { ns: "event_definitions" }),
      i18n.t("hook_notifications.cluster_config_failed_title", { ns: "event_definitions" })
    )
  });
  return {
    eventsClusterConfig: isLoading ? {} : data || {},
    loadingEventsClusterConfig: isLoading
  };
}
